import { createSlice } from '@reduxjs/toolkit'
import type { ILatLng, ISearchState, IFilterListingsResponse } from './types'
import type { PayloadAction } from '@reduxjs/toolkit'
import {
  updateActivePage,
  updateListings,
  updateTypedLocation,
} from './actions'

// Define the initial state using that type
const initialState: ISearchState = {
  bounds: null,
  boundslistings: [],
  currentPageNumber: 1,
  locationNotFound: false,
  isModalShareOpen: false,
  lastCenterLat: 0,
  lastCenterLng: 0,
  lastCenterRadius: 50,
  lastParams: '',
  listings: [],
  searchIndex: 0,
  initialSelectedLocation: -1,
  selectedLocation: -1,
  shouldRequery: false,
  streetMapData: {},
  typedLocation: '',
  isFirstLoad: true,
  filtered: false,
  triangle: [],
  boundsFilterIndex: 0,
  pendingSearchIndex: 0,
  pendingBoundsFilterIndex: 0,
}

export const searchSlice = createSlice({
  name: 'search',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    startSearch: (state) => {
      state.pendingSearchIndex += 1
      state.pendingBoundsFilterIndex += 1
    },
    startBoundsFilter: (state) => {
      state.pendingBoundsFilterIndex += 1
    },
    updateLocationNotFound: (state, action: PayloadAction<boolean>) => {
      state.locationNotFound = action.payload
    },
    updateModalShareState: (state, action: PayloadAction<boolean>) => {
      state.isModalShareOpen = action.payload
    },
    updateLatLng: (state, action: PayloadAction<ILatLng>) => {
      state.lastCenterLat = action.payload.lat
      state.lastCenterLng = action.payload.lng
    },
    updateFirstLoad: (
      state,
      action: PayloadAction<boolean | null | undefined>
    ) => {
      state.isFirstLoad = action.payload ?? false
    },
    updateRequeryStatus: (state, action: PayloadAction<boolean>) => {
      state.shouldRequery = action.payload
    },
    updateTriangle: (state, action: PayloadAction<any>) => {
      state.triangle = action.payload
    },
    updateBounds: (state, action: PayloadAction<IFilterListingsResponse>) => {
      if (action.payload.boundsFilterIndex === state.boundsFilterIndex) {
        let newboundslistings = [
          ...state.boundslistings,
          ...action.payload.boundslistings,
        ]

        try {
          if (
            state.boundslistings[0]?.id === state.initialSelectedLocation &&
            state.boundslistings.length === 1 &&
            action.payload.boundslistings.length > 0
          ) {
            newboundslistings = action.payload.boundslistings
          }
        } catch (error) {
          console.log({ error })
        }
        return {
          ...state,
          boundslistings: newboundslistings,
        }
      } else if (action.payload.boundsFilterIndex > state.boundsFilterIndex) {
        const newlistings = action.payload.boundslistings
        state.boundslistings = newlistings
        state.boundsFilterIndex = action.payload.boundsFilterIndex
      } else return state
    },
  },
  extraReducers: (builder) => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder
      .addCase(updateListings.fulfilled, (state, action) => {
        const { listings, newParams, searchIndex } = action.payload
        if (searchIndex < state.pendingSearchIndex) {
          return state
        }
        const params = newParams === '' ? {} : JSON.parse(newParams!)

        const { radius: paramRadius } = params

        return {
          ...state,
          currentPageNumber: 1,
          lastCenterRadius: paramRadius,
          lastParams: newParams === '' ? state.lastParams : newParams!,
          listings,
          searchIndex,
        }
      })
      .addCase(updateActivePage.fulfilled, (state, action) => {
        state.currentPageNumber = action.payload
      })
      .addCase(updateTypedLocation.fulfilled, (state, action) => {
        return {
          ...state,
          shouldRequery: true,
          ...action.payload,
        }
      })
      .addMatcher(
        (action) =>
          action.type.startsWith('search') &&
          action.type.endsWith('fulfilled') &&
          ![
            updateListings.fulfilled.type,
            updateActivePage.fulfilled.type,
            updateTypedLocation.fulfilled.type,
          ].includes(action.type),
        (state, action: any) => {
          return {
            ...state,
            ...action.payload,
          }
        }
      )
  },
})

export const {
  startSearch,
  startBoundsFilter,
  updateLocationNotFound,
  updateModalShareState,
  updateLatLng,
  updateFirstLoad,
  updateRequeryStatus,
  updateTriangle,
  updateBounds,
} = searchSlice.actions

export default searchSlice.reducer
